import { useMemo, useState } from "react"
import type { TileDisplay, TileResult, WidgetType } from "@csbi/contracts"
import type { MetricDefinition } from "@csbi/metric-catalog"
import {
  MAP_ZOOM_MAX,
  MAP_ZOOM_MIN,
  buildVizModel,
  formatValue,
  hasStale,
  isChartWidget,
  offMapEntries,
  optionFor,
  zoomIn,
  zoomOut,
  type MapView,
  type ThemeMode,
  type VizModel,
} from "@csbi/viz-core"
import { EChartsBase } from "./echarts-base.js"
import { WIDGET_LABELS } from "./labels.js"
import { useWorldMap } from "./use-world-map.js"

export type ChartWidgetProps = {
  result: TileResult
  widget: WidgetType
  display?: Partial<TileDisplay>
  definitions: ReadonlyMap<string, MetricDefinition>
  mode: ThemeMode
  title: string
  height?: number | string
  dimmed?: boolean
  onSeriesToggle?: (name: string, visible: boolean) => void
  onDrill?: (payload: { seriesName: string; category: string; value: number | null }) => void
  categoryLabel?: (raw: string) => string
}

export function useVizModel(
  result: TileResult,
  widget: WidgetType,
  definitions: ReadonlyMap<string, MetricDefinition>,
  mode: ThemeMode,
  categoryLabel?: (raw: string) => string,
): VizModel {
  return useMemo(
    () =>
      buildVizModel(result, {
        mode,
        widget,
        definitions,
        granularity: result.meta.resolvedRange.granularity,
        ...(categoryLabel ? { categoryLabel } : {}),
      }),
    [result, widget, definitions, mode, categoryLabel],
  )
}

/**
 * ⚠ **Position alone is not enough, because not every chart puts its categories along an axis.** A gauge grid
 * is **one series per category** (see `options/gauge.ts`), so every panel reports `dataIndex: 0` and carries its
 * identity in `seriesName` instead. Reading position there would have drilled the first module whichever panel
 * was clicked — seven gauges, one answer, and nothing on screen to say so.
 */
export function rawCategoryFor(
  model: VizModel,
  click: { dataIndex: number; category: string; seriesName: string },
): string {
  const unique = (label: string) => {
    if (!label) return undefined
    const hits = model.categories.flatMap((c, i) => (c === label ? [i] : []))
    return hits.length === 1 ? model.rawCategories[hits[0]!] : undefined
  }
  return (
    unique(click.category) ?? unique(click.seriesName) ?? model.rawCategories[click.dataIndex] ?? click.category
  )
}

export function ChartWidget({
  result,
  widget,
  display,
  definitions,
  mode,
  title,
  height = "100%",
  dimmed,
  onSeriesToggle,
  onDrill,
  categoryLabel,
}: ChartWidgetProps) {
  const model = useVizModel(result, widget, definitions, mode, categoryLabel)
  const basemap = useWorldMap(model.widget === "map")
  const [view, setView] = useState<MapView>({ zoom: MAP_ZOOM_MIN })

  // Destructure before the memo: callers routinely pass object literals, so depending on `display` directly would defeat it
  const { stacked, area, showDataLabels, donut, legendPosition } = display ?? {}
  const option = useMemo(
    () =>
      optionFor(
        model,
        mode,
        {
          ...(stacked == null ? {} : { stacked }),
          ...(area == null ? {} : { area }),
          ...(showDataLabels == null ? {} : { showDataLabels }),
          ...(donut == null ? {} : { donut }),
          ...(legendPosition == null ? {} : { legendPosition }),
        },
        { knownRegions: basemap.regions, mapView: view },
      ),
    [model, mode, stacked, area, showDataLabels, donut, legendPosition, basemap.regions, view],
  )

  if (!isChartWidget(model.widget)) return null

  if (model.widget === "map" && basemap.status !== "ready")
    return <BasemapNotice status={basemap.status} {...(basemap.error ? { error: basemap.error } : {})} />

  const chart = (
    <EChartsBase
      option={option}
      mode={mode}
      height={height}
      dimmed={dimmed}
      stale={hasStale(model)}
      ariaLabel={ariaLabelFor(title, model, result)}
      onSeriesToggle={onSeriesToggle}
      {...(onDrill
        ? {
            onMarkClick: ({ dataIndex, category, seriesName, ...rest }) =>
              onDrill({
                ...rest,
                seriesName,
                category: rawCategoryFor(model, { dataIndex, category, seriesName }),
              }),
          }
        : {})}
      {...(model.widget === "map" ? { onGeoRoam: setView } : {})}
    />
  )

  if (model.widget !== "map") return chart

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
        {chart}
        <MapZoom view={view} onChange={setView} />
      </div>
      <OffMapList model={model} known={basemap.regions} />
    </div>
  )
}

function MapZoom({ view, onChange }: { view: MapView; onChange: (v: MapView) => void }) {
  const atMin = view.zoom <= MAP_ZOOM_MIN + 1e-6
  const atMax = view.zoom >= MAP_ZOOM_MAX - 1e-6

  // Clear the centre along with the zoom, back to the initial view `boundingCoords` decides —
  // restoring only the zoom leaves a "scale is 1 but the view sits off to one side" state the reader cannot undo
  const set = (zoom: number) =>
    onChange(zoom <= MAP_ZOOM_MIN + 1e-6 ? { zoom: MAP_ZOOM_MIN } : { ...view, zoom })

  return (
    <div className="csbi-map-zoom">
      <button
        type="button"
        className="csbi-map-zoom__btn"
        aria-label="Zoom in"
        title="Zoom in"
        disabled={atMax}
        onClick={() => set(zoomIn(view.zoom))}
      >
        +
      </button>
      <button
        type="button"
        className="csbi-map-zoom__btn"
        aria-label="Zoom out"
        title="Zoom out"
        disabled={atMin}
        onClick={() => set(zoomOut(view.zoom))}
      >
        −
      </button>
    </div>
  )
}

function BasemapNotice({ status, error }: { status: string; error?: string }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        minHeight: 120,
        fontSize: 12,
        color: "var(--csbi-ink-muted)",
        padding: "0 16px",
        textAlign: "center",
      }}
    >
      {status === "error"
        ? `The world basemap could not be loaded${error ? ` (${error})` : ""}. The table view has the same numbers.`
        : "Loading the world basemap…"}
    </div>
  )
}

function OffMapList({ model, known }: { model: VizModel; known: ReadonlySet<string> }) {
  const entries = offMapEntries(model, known)
  if (entries.length === 0) return null

  return (
    <div
      style={{
        flex: "0 0 auto",
        borderTop: "1px solid var(--csbi-gridline)",
        padding: "6px 8px 2px",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "baseline",
        gap: "4px 12px",
        fontSize: 11,
        lineHeight: 1.6,
      }}
    >
      <span style={{ color: "var(--csbi-ink-muted)" }}>Not on the map:</span>
      {entries.map((e) => (
        <span key={e.code} style={{ display: "inline-flex", alignItems: "baseline", gap: 4 }}>
          <span style={{ color: "var(--csbi-ink-secondary)" }}>{e.code}</span>
          <strong style={{ color: "var(--csbi-ink-primary)", fontVariantNumeric: "tabular-nums" }}>
            {formatValue(e.values.find((v) => v != null) ?? null, {
              valueType: model.unit,
              context: "label",
            })}
          </strong>
        </span>
      ))}
    </div>
  )
}

function ariaLabelFor(title: string, model: VizModel, result: TileResult): string {
  const from = result.meta.resolvedRange.from.slice(0, 10)
  const to = result.meta.resolvedRange.to.slice(0, 10)
  const omitted = model.omitted.length ? `, ${model.omitted.length} deployment(s) with no data` : ""
  return `${WIDGET_LABELS[model.widget]}: ${title}, ${from} to ${to}, ${model.series.length} series${omitted}`
}
